export interface Dependence {
  status: 'NOT_EVALUATED' | 'OK' | 'WATCH' | 'EXCEEDED' | 'BLOCKED';
  totalCents: number;
  top: { key: string; name: string; cents: number; sharePct: number } | null;
  thresholdPct: number;
  warnPct: number;
  minRevenueCents: number;
  graceEndsAt: string | null;
  enforced: boolean;
}

export interface UrssafPeriod {
  key: string;
  label: string;
  start: string;
  end: string;
  deadline: string;
  totalCents: number;
  declarableCents: number;
  submittedViaApp?: boolean;
  contributionCents?: number | null;
  inAppCents: number;
  outOfAppCents: number;
  entries: number;
  status: 'OPEN' | 'TO_DECLARE' | 'OVERDUE' | 'DECLARED';
  declaredAt: string | null;
  declaredAmountCents: number | null;
  complementCents: number;
}

export interface Overview {
  emailReminders: boolean;
  attestation: { valid: boolean; reason: 'NONE' | 'EXPIRED' | 'OUTDATED' | null; attestedAt: string | null; expiresAt: string | null; text: string; version: string };
  dependence: Dependence;
  billing: { complete: boolean; missing: string[]; mandateAccepted: boolean; pendingInvoices: number };
  urssaf: { frequency: 'MONTHLY' | 'QUARTERLY'; toDeclare: UrssafPeriod | null; current: UrssafPeriod | null };
}

export interface Party {
  name?: string;
  tradeName?: string | null;
  siret?: string | null;
  address?: string | null;
  postalCode?: string | null;
  city?: string | null;
  vatRegime?: 'FRANCHISE' | 'LIABLE';
  vatNumber?: string | null;
}

export interface ExtraInvoice {
  id: string;
  number: string;
  source: 'IN_APP' | 'OFF_APP';
  status: 'ISSUED' | 'PAID';
  issuer: Party;
  client: Party;
  lines: { description: string; quantity: number; unitPriceCents: number; totalCents: number }[];
  issuedVia: { mode: 'MANDATE' | 'SELF_SERVICE'; platformName: string; site: string };
  subtotalCents: number;
  vatRatePct: number;
  vatCents: number;
  totalCents: number;
  serviceDate: string;
  issuedAt: string;
  dueDate: string | null;
  paidAt: string | null;
  paidMethod: string | null;
  mandateAcceptedAt: string | null;
  kind: 'INVOICE' | 'CREDIT_NOTE';
  creditReason: string | null;
  creditedInvoiceId: string | null;
  creditedNumber: string | null;
  creditNoteId: string | null;
  creditNoteNumber: string | null;
}

export interface Delegation {
  available: boolean;
  status: 'NONE' | 'ACTIVE' | 'REVOKED';
  version: string;
  text: string;
  activatedAt: string | null;
}

export interface Client {
  id: string;
  name: string;
  siret: string | null;
  address: string | null;
  postalCode: string | null;
  city: string | null;
  email: string | null;
}

export interface Revenue {
  id: string;
  source: 'IN_APP' | 'OFF_APP' | 'EXTERNAL';
  clientName: string;
  amountCents: number;
  receivedAt: string;
  reference: string | null;
  invoiceId: string | null;
  invoiceNumber: string | null;
}

export interface BillingState {
  profile: { legalName: string; tradeName: string; siret: string; address: string; postalCode: string; city: string; vatRegime: 'FRANCHISE' | 'LIABLE' | null; vatNumber: string };
  missing: string[];
  mandate: { version: string; text: string; acceptedAt: string | null };
}

export const fmtDay = (iso: string) => new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString('fr-FR');

export const toCents = (value: string) => Math.round(Number(value.replace(',', '.')) * 100);

export const SOURCE_LABELS: Record<Revenue['source'], string> = {
  IN_APP: 'Mission Mon Extra',
  OFF_APP: 'Hors app · facture générée',
  EXTERNAL: 'Hors app · facture externe',
};
